import { graphql } from "react-relay";
// other components
import { ChromeCreateEvent } from "@components/chrome-create-event";
import { ChromeLogin } from "@components/chrome-login";
import { ChromeMenuDrawer } from "@components/chrome-menu-drawer";
import { ChromePersonalCalendar } from "@components/chrome-personal-calendar";
import { FeedScheduler } from "@components/feed-scheduler";
import { SettingsDialog } from "@components/settings-dialog";
import { FastCrawlLoader } from "@components/fast-crawl-loader";
import { TutorialBox } from "@components/tutorial-box";
import { FastCrawlErrorCard } from "@components/fast-crawl-error-card";
import { FlowStatePoller } from "@components/flow-state-poller";
import "@components/onboarding-nux/OnboardingNUXRenderer.gql";
import "@components/pricing-dialog/PricingDialog.gql";
import "@components/freemium-status-callout/FreemiumStatusCallout.gql";
import "@components/plan-status-refetch/PlanStatusRefetch.gql";

export const chromeWrapperFragments = {
  viewer: graphql`
    fragment ChromeWrapper_viewer on Viewer {
      ...ChromeLogin_viewer
      ...ChromeMenuDrawer_viewer
      user {
        ...ChromePersonalCalendar_user
        ...TutorialBox_user
        ...FeedScheduler_user
        id
        status
        emails
        givenName
        familyName
        createdTime
        linkNameErrorable {
          ... on GraphEntityError {
            message
            statusCode
          }
          ... on LinkName {
            __typename
            linkName
          }
        }
        schedulingLinks {
          linkName
          linkUrl
          meetingSettings {
            id
            timingSettings {
              durationMinutes
              timeRange {
                windowSizeDays
              }
            }
            name
            slug
            isActive
            availabilityType
          }
        }
        members {
          value
          scope {
            type
            value
          }
        }
        webAppFlagsErrorable {
          __typename
          ... on GraphEntityError {
            statusCode
            message
          }
          ... on Flags {
            flags
          }
        }
        chromeExtensionFlagsErrorable {
          __typename
          ... on GraphEntityError {
            statusCode
            message
          }
          ... on Flags {
            flags
          }
        }

        # FLOWSTATES
        flowStates(first: 1000000) {
          edges {
            node {
              ...FastCrawlLoader_flowState
              ...FlowStatePoller_flowState
              id
              flowKey
              previousState
              current {
                state
                percentComplete
                errorMessage
              }
              pending {
                state
                percentComplete
                errorMessage
              }
            }
          }
        }

        # DATASOURCES
        dataSources(first: 1000000) {
          edges {
            node {
              id
              state
              type
              name
            }
          }
        }

        # ORG
        orgs(first: 1000000) {
          edges {
            node {
              ...OnboardingNUXRenderer_org
              ...ChromePersonalCalendar_org
              ...FastCrawlErrorCard_org
              ...FeedScheduler_org
              ...PricingDialog_org
              ...FreemiumStatusCallout_org
              ...ChromeCreateEvent_org @arguments(calendarIds: [])
              ...PlanStatusRefetch_org
              id
              name
              domains
              createdTime
              crawlTest
              experiments
              primaryOrgEmail
              primaryOrgCalendar
              userWorkingHours(resolveOverrides: false) {
                workingHours {
                  id
                  type
                  timeZone
                  daySettings {
                    day
                    setting {
                      minSlot
                      maxSlot
                      excludedSlots
                    }
                  }
                  dayOverrides {
                    date
                    setting {
                      minSlot
                      maxSlot
                      excludedSlots
                    }
                  }
                }
              }
              orgMeetingHours {
                workingHours {
                  timeZone
                }
              }
              shadowCalendarSettingsErrorable {
                __typename
                ... on ShadowCalendarSettings {
                  enabled
                  shadowCalendarId
                  focusTimeIdealMinutesPerWeek
                  focusTimeSyncEnabled
                  focusTimeSyncAll
                }
              }
              flowStates(first: 1000000) {
                edges {
                  node {
                    ...FastCrawlLoader_flowState
                    ...FlowStatePoller_flowState
                    id
                    flowKey
                    previousState
                    current {
                      state
                      percentComplete
                      errorMessage
                    }
                    pending {
                      state
                      percentComplete
                      errorMessage
                    }
                  }
                }
              }
              primaryTeam {
                __typename
                ... on Team {
                  teamId
                  teamName
                }
                ... on GraphEntityError {
                  statusCode
                  message
                }
              }
              userTeams {
                __typename
                ... on TeamList {
                  list {
                    teamId
                    teamCalendarId
                    userSettings {
                      __typename
                      ... on TeamUserSettings {
                        useTeamCalendar
                      }
                    }
                    settings {
                      __typename
                      ... on TeamSettings {
                        manageTeamCalendar
                      }
                    }
                  }
                }
              }
              orgAccountingPeriod {
                __typename
                ... on OrgAccountingPeriod {
                  periodRange {
                    minDate
                    maxDate
                  }
                  quotaMax
                  quotaUsed
                  enforcementState
                }
                ... on GraphEntityError {
                  statusCode
                  message
                }
              }
              userGoals(first: 1000000) {
                edges {
                  node {
                    id
                    goalId
                    name
                    isDeleted
                    options {
                      specificOptions {
                        ... on GoalWithinDayOptions {
                          minTimeSlot
                          maxTimeSlot
                          minimumDuration
                          idealDuration
                        }
                        ... on GoalWithinWeekOptions {
                          maxMinutesPerBlock
                        }
                      }
                    }
                  }
                }
              }
              ...EventColoringSettings_org
              eventColoringSettingsErrorable {
                __typename
                ... on GraphEntityError {
                  statusCode
                  message
                }
                ... on EventColoringSettings {
                  eventCategoryColorings {
                    ...EventCategoryColoring_eventCategoryColoring
                    googleColorKey
                    eventCategory
                    active
                    isUserSet
                  }
                }
              }
            }
          }
        }
      }
    }
  `,
};

export const chromeWrapperQuery = graphql`
  query ChromeWrapperQuery {
    viewer {
      ...ChromeWrapper_viewer
    }
  }
`;
